import { makeAutoObservable } from 'mobx'
import dayjs from 'dayjs'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { type ISearchDropdownStoreOptions } from 'widgets/SearchDropdown/store/types'

export class SearchDropdownTimeRangeStore {
  private _fromTime: Date | null = null
  private _toTime: Date | null = null
  private _disableClosing = false
  private _options: ISearchDropdownStoreOptions | null = null

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  selectStartTime = (time: Date | null) => {
    this._fromTime = time
  }

  selectEndTime = (time: Date | null) => {
    this._toTime = time
  }

  initOptions = (options: ISearchDropdownStoreOptions) => {
    this._options = options
  }

  resetType = () => {
    this._fromTime = null
    this._toTime = null
  }

  setDisableClosing = (disableClosing: boolean) => {
    this._disableClosing = disableClosing
  }

  private _parseToTimeRangeInput = (timeString: string): Date | null => {
    const parsed = dayjs(timeString, 'HH:mm:ss')
    if (!parsed.isValid()) {
      return null
    }

    // Only hours are allowed, minutes, seconds and milliseconds are ignored
    return parsed.minute(0).second(0).millisecond(0).toDate()
  }

  setBusinessHours = () => {
    if (this._fromTime !== null || this._toTime !== null) {
      return
    }

    const {
      usersStore: { businessHours },
    } = this._entities
    if (!businessHours || !businessHours.is_active) {
      return
    }

    const mondayData = businessHours.days.find((businessHoursDay) => businessHoursDay.day === 'mon')
    if (!mondayData) {
      return
    }

    const fromTime = this._parseToTimeRangeInput(mondayData.start)
    const toTime = this._parseToTimeRangeInput(mondayData.end)

    if (fromTime && toTime) {
      const fromTimeValue = fromTime.getTime()
      const toTimeValue = toTime.getTime()

      if (fromTimeValue < toTimeValue) {
        this._fromTime = fromTime
        this._toTime = toTime
      }
    }
  }

  get searchParams() {
    if (this._fromTime === null || this._toTime === null) return null

    return {
      fromTime: this._fromTime,
      toTime: this._toTime,
    }
  }

  get disableClosing() {
    return this._disableClosing
  }

  get hasSelectedItems() {
    return this.searchParams !== null
  }

  get fromTime() {
    return this._fromTime
  }

  get toTime() {
    return this._toTime
  }

  get options() {
    return this._options
  }

  get filteredRemainingTime() {
    return this._toTime
      ? uiStore.dayjsWithoutTimezone(this._toTime).add(-1, 'second').toDate()
      : undefined
  }

  get filteredPassedTime() {
    return this._fromTime
      ? uiStore.dayjsWithoutTimezone(this.fromTime).add(1, 'second').toDate()
      : undefined
  }
}
